import * as angular from 'angular';
import { List, Map, OrderedMap } from 'immutable';
import { formatDistance } from 'date-fns';

import { AppStore } from '~/types/main.types';

import { OrganizationResource, SpaceResource } from '@mode/legacy/data-access-model-js';
import { ReportResource } from '@mode/shared/util-halogen';
import { MenuOptionId, MenuOptionRecord } from '@mode/legacy/types-app-state';
import { DisplayMode, ReportTypes } from '@mode/shared/contract-common';
import { DatasourceDomain } from '@mode/shared/contract-common';
import { ToastService } from '@mode/shared/util-toast';
import { AccountFacade } from '@mode/accounts/contract';

import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { DataViewFacade } from '@mode/switch-views/contract';
import { SessionFacade } from '@mode/sessions/domain';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { OrganizationHelpers } from '@mode/legacy/data-access-model-js';
import { ReportDataService } from '~/services/report-data-service';
import { WindowActions } from '~/actions/window.actions';
import { map, takeUntil } from 'rxjs/operators';
import { pick, uniq, map as _map } from 'lodash';
import { SchedulesModalService } from '@mode/shared/ui-angularjs';
import { getTrialBanner, getThoughtspotIntegrated } from '@mode/shared/util-js';
import {
  EmailFailureDialogService,
  ShareDialogEmailForm,
  ShareDialogService,
  ShareDialogData,
  ShareDialogSlackForm,
} from '@mode/reports/ui';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { GithubFacade } from '@mode/github/contract';
import { ReportCloneFacade } from '@mode/reports/domain';

type AnyMenuOptionType = MenuOptionRecord<MenuOptionId.AllTypes>;
type AnyMenuOptionOrderedMapType = OrderedMap<MenuOptionId.AllTypes, AnyMenuOptionType | List<AnyMenuOptionType>>;
type AnyMenuOptionArrayType = Array<AnyMenuOptionType | List<AnyMenuOptionType>>;

export enum PlatformHeaderNavMenuType {
  Report = 'Report',
  Editor = 'Editor',
  DefinitionEditor = 'DefinitionEditor',
  Dataset = 'Dataset', // @TODO: Determine if we need to differentiate between DatasetEditor as well here
}

type LimitMap = {
  [menuType in PlatformHeaderNavMenuType]: number;
};

/**
 * @todo fix key typing and replace string with proper type like {@link PlatformHeaderNavMenuType}
 *   - useful conversation around using enums as keys https://github.com/Microsoft/TypeScript/issues/2491
 */
interface MenuSchema {
  // menuType: PlatformHeaderNavMenuType
  [menuType: string]: {
    // displayMode: DisplayMode
    [displayMode: string]: MenuSchemaEntryOrderedMap;
  };
}
type MenuSchemaEntry = [MenuOptionId.AllTypes, MenuSchemaEntryOptions];
type PartialMenuSchemaEntry = [MenuOptionId.AllTypes] | MenuSchemaEntry;
/**
 * @todo fix typing and remove any union
 * @note have to use "any" because it doesn't like arrays :/ for
 * OrderedMaps that are generated from arrays like we generate
 * in {@link PlatformHeaderNavController#navSchema()}
 */
type MenuSchemaEntryOrderedMap = OrderedMap<MenuOptionId.AllTypes, MenuSchemaEntryOptions | any>;
type MenuSchemaEntrySimpleSet = PartialMenuSchemaEntry[][];
interface MenuSchemaEntryOptions {
  name?: string;
  children?: MenuSchemaEntrySimpleSet;
}

export class PlatformHeaderNavController {
  account: any; // ModeModel
  options: Map<string, AnyMenuOptionType>;
  expectedRuntime: number;
  displayMode: DisplayMode;
  hasUnsavedChanges: boolean;
  hasUnsyncedChanges: boolean;
  orderedDropdownOptions: AnyMenuOptionOrderedMapType;
  orderedOverflowOptions: AnyMenuOptionOrderedMapType;
  orderedVisibleOptions: AnyMenuOptionOrderedMapType;
  orderedDropdownOptionsArray: AnyMenuOptionArrayType;
  orderedOverflowOptionsArray: AnyMenuOptionArrayType;
  orderedVisibleOptionsArray: AnyMenuOptionArrayType;
  lastSyncedAt: Date;
  navType: PlatformHeaderNavMenuType;
  mobileMenuIsOpen: boolean;

  onSimpleShare: () => void;
  onViewDetails: () => void;
  onViewReport: () => void;
  onReportSpaceTransfer: (args: {
    $space: SpaceResource;
    isViaPublishAction: boolean;
    switchViewToken?: string;
  }) => void;
  onRunReport: () => void;
  onReportDeleted: (args: { $reportToken: string }) => void;
  onDefinitionCreated: (args: { $redirectLink: string }) => void;
  onDefinitionUpdate: (args: { $definitionModel: any }) => void;
  onDefinitionDelete: () => void;
  onMobileMenuToggle: (args: { $isOpen: boolean }) => void;
  onSaveClick: () => void;

  organizations: OrganizationResource[];
  owner: OrganizationResource;
  owner$: BehaviorSubject<OrganizationResource> | undefined = new BehaviorSubject(undefined);
  trialLink: string;
  shareDialogData$: Observable<ShareDialogData>;
  report: ReportResource;
  reportRun: any;
  reportRunAt: Date;
  reportSpace: any;
  staleRunAtEnabled = false;
  dbtData: ReportTypes.ReportDbtMetaData | undefined;
  referencedDatasources: DatasourceDomain.Datasource[];
  editAccess = false;
  reportOwnerWithSpaces$: Observable<any>;
  loggedOutTracking = false;
  signedIn = true;
  isTrialExpired = false;
  disableRefreshButton = false;
  thoughtspotIntegrated: boolean;

  // temp feature flags
  reportStateEnabled$: Observable<boolean>;
  redirectToNotebook = false;

  static $inject = [
    '$element',
    '$window',
    '$timeout',
    'RxStoreService',
    'AccountFacade',
    'FeatureFlagsFacade',
    'ShareDialogService',
    'EmailFailureDialogService',
    'ReportDataService',
    'ReportRunEmailModel',
    'ToastService',
    'SchedulesModalService',
    'AnalyticsService',
    'SessionFacade',
    'ReportRunSlackMessageModel',
    'ObservabilityService',
    'GithubFacade',
    'ReportCloneFacade',
    'DataViewFacade',
  ];
  private destroy$ = new Subject<void>();

  constructor(
    private $element,
    private $window,
    private $timeout,
    private store: AppStore,
    private accountFacade: AccountFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private shareDialogService: ShareDialogService,
    private emailFailureDialogService: EmailFailureDialogService,
    private reportDataService: ReportDataService,
    private reportRunEmailModel,
    private toastService: ToastService,
    private schedulesModalService: SchedulesModalService,
    private analyticsService: AnalyticsService,
    private sessionFacade: SessionFacade,
    private ReportRunSlackMessageModel,
    private observabilityService: ObservabilityService,
    private githubFacade: GithubFacade,
    private reportCloneFacade: ReportCloneFacade,
    private dataViewFacade: DataViewFacade
  ) {}

  $onInit() {
    this.navType = this.navType || PlatformHeaderNavMenuType.Report;

    this.reportStateEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.SkipReportAccountResolver);

    this.featureFlagsFacade
      .asObservable(FeatureFlag.LoggedOutTracking)
      .subscribe((enabled) => (this.loggedOutTracking = enabled));

    this.sessionFacade.sessionIsActive$.subscribe(
      (sessionIsActiveData: boolean): boolean => (this.signedIn = sessionIsActiveData)
    );

    this.reportOwnerWithSpaces$ = combineLatest([this.accountFacade.reportOwner$, this.owner$]).pipe(
      map(([reportOwner, owner]) => ({
        ...reportOwner,
        ...pick(owner, ['spaces', 'new_spaces', 'modelFromForm_spaces', '_resource']),
      }))
    );

    if (getTrialBanner(this.$window.document) === 'trial_expired') {
      this.isTrialExpired = true;
    }
    this.thoughtspotIntegrated = getThoughtspotIntegrated(this.$window.document);
    combineLatest([
      this.dataViewFacade.importedDatasetCount$,
      this.dataViewFacade.localSwitchViewCount$,
      this.featureFlagsFacade.asObservable(FeatureFlag.CreateNotebookWithNoQuery),
    ])
      .pipe(
        map(
          ([importedDatasetcount, queryCount, sqlFreeNotebookEnabled]) =>
            importedDatasetcount === 0 && queryCount === 0 && sqlFreeNotebookEnabled
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((disabled) => {
        this.disableRefreshButton = disabled;
        this.redirectToNotebook = disabled;
      });
  }

  isReportCloneInProgress$(reportId: string): Observable<boolean> {
    return this.reportCloneFacade.reportCloneLoaded$(reportId).pipe(map((reportCloneLoaded) => !reportCloneLoaded));
  }

  $postLink() {
    this.observabilityService.recordSpanTiming(ObservabilityEvents.HEADER_LOADED, ObservabilityEvents.HEADER_LOADING);
  }

  /**
   * @param changed
   */
  $onChanges(changed) {
    if (changed.options) {
      const visibleLimit = this.navMenuVisibleLimit();

      const getAndMaybeRenameOption = ([id, opts = {}]: MenuSchemaEntry) => {
        if (opts.name) {
          return this.options.get(id).set('name', opts.name);
        } else {
          return this.options.get(id);
        }
      };

      const entryToOption = ([id, opts = {}]: MenuSchemaEntry): [
        MenuOptionId.AllTypes,
        List<AnyMenuOptionType> | AnyMenuOptionType,
      ] => {
        const { children, name } = opts;

        if (children && Array.isArray(children) && children.length) {
          return [id, List([[id, opts], ...children].map(getAndMaybeRenameOption))];
        } else {
          return [id, getAndMaybeRenameOption([id, opts])];
        }
      };

      const granted = (_, optionId: MenuOptionId.AllTypes): boolean => this.options.get(optionId).isGranted();

      this.$timeout(() => {
        this.orderedDropdownOptions = this.navSchema(DisplayMode.Dropdown).mapEntries(entryToOption);
        this.orderedVisibleOptions = this.navSchema(DisplayMode.Menu)
          .take(visibleLimit)
          .filter(granted)
          .mapEntries(entryToOption);
        this.orderedOverflowOptions = this.navSchema(DisplayMode.Menu)
          .skip(visibleLimit)
          .filter(granted)
          .mapEntries(entryToOption);
        this.orderedDropdownOptionsArray = this.orderedDropdownOptions.valueSeq().toArray();
        this.orderedOverflowOptionsArray = this.orderedOverflowOptions.valueSeq().toArray();
        this.orderedVisibleOptionsArray = this.orderedVisibleOptions.valueSeq().toArray();
      });
    }

    if (changed.owner) {
      this.trialLink = OrganizationHelpers.getTrialLink(changed.owner.currentValue._resource);
      if (this.owner) {
        this.owner$.next(this.owner);
      }
    }
  }

  navMenuVisibleLimit(): number {
    const limitMap: LimitMap = {
      Report: 5,
      Editor: 5,
      DefinitionEditor: 2,
      Dataset: 0,
    };
    const limit: number = limitMap[this.navType] || limitMap.Report;
    const found: number[] = [];
    let result: number;

    this.navSchema(DisplayMode.Menu)
      .keySeq()
      .toArray()
      .some((optionKey, index) => {
        if (this.options.get(optionKey).isGranted()) {
          found.push(index);
        }
        return found.length === limit;
      });

    result = (found.pop() || 0) + 1;

    return Math.max(result, limit);
  }

  handleDefinitionCreated($definitionModel) {
    let $redirectLink: string = null;
    if ($definitionModel) {
      $redirectLink = $definitionModel['editLink'];
    } else {
      // @todo consider logging an error/warning here
    }

    this.onDefinitionCreated({ $redirectLink });
  }

  handleDefinitionUpdate($definitionModel) {
    if ($definitionModel) {
      this.onDefinitionUpdate({ $definitionModel });
    }
  }

  handleDefinitionDelete($evt) {
    this.onDefinitionDelete();
  }

  handleSimpleShareClick($evt) {
    this.onSimpleShare();
  }

  handleViewDetailsClick($evt) {
    this.onViewDetails();
  }

  handleViewReportClick($evt) {
    this.onViewReport();
  }

  handleRunReportClick($evt) {
    const runOption: MenuOptionRecord<MenuOptionId.Report.Run> = this.options.get(MenuOptionId.Report.Run, undefined);

    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: 'Report View',
      action: 'Run Now',
      context: 'report_run',
      context_id: this.reportRun.token,
      // track user sign_in status
      ...(this.loggedOutTracking && { signed_in: this.signedIn }),
    });

    if (runOption && runOption.isEnabled() && runOption.isGranted()) {
      this.onRunReport();

      this.disableRefreshButton = true;
    }
  }

  handleReportDeleted($reportToken) {
    this.onReportDeleted({ $reportToken });
  }

  handleOpenViewDetails() {
    this.onViewDetails();
  }

  handleReportSpaceTransfer($space, isViaPublishAction, switchViewToken) {
    this.onReportSpaceTransfer({ $space, isViaPublishAction, switchViewToken });
  }

  handleSave() {
    this.onSaveClick();
  }

  formatSlackMentions(message: string): string {
    return message.replace(/(@\w+)/gi, '<$1>');
  }

  handleShare(): void {
    const sendingEmail$ = new BehaviorSubject<boolean>(false);
    this.shareDialogService.open({
      data$: this.shareDialogData$,
      sendingEmail$: sendingEmail$.asObservable(),
      toggleExternalShareAction: (state: boolean) => this.reportDataService.updateSelectedReportSharedState(state),
      sendEmail: ({ recipients, message, options }: ShareDialogEmailForm) => {
        sendingEmail$.next(true);
        this.reportRunEmailModel
          .create({
            reportRun: this.reportRun.token,
            report: this.report.token,
            params: this.createEmailParams({
              recipients,
              message,
              options,
            }),
          })
          .then((result) => {
            this.shareDialogService.close();
            const failures = uniq(_map(result._embedded.failed, 'address'));
            if (failures.length) {
              this.emailFailureDialogService.open({ failures });
            } else {
              this.toastService.success('Email sent');
            }
          });
      },
      scheduleDelivery: () => {
        /*
        Overlaying modals on top of each other cause issues with our new select components,
        that is why we close the dialog before opening the schedule modal on the next line
        */
        this.shareDialogService.close();
        this.schedulesModalService.openFromShareModal({
          report: this.report,
          run: this.reportRun,
          reportOwner: this.owner,
          account: this.account,
        });
      },
      sendSlackMessage: ({ message, channel }: ShareDialogSlackForm) => {
        const slackObject = this.reportRun.modelFromForm_reportRunSlackMessages();
        slackObject.channel = channel.channelName;

        if (typeof channel.id === 'string') {
          slackObject.channel_id = channel.id;
        }

        // enable @mentions by wrapping in < >
        slackObject.message = this.formatSlackMentions(message);

        this.ReportRunSlackMessageModel.create({
          reportRun: this.reportRun.token,
          report: this.report.token,
          params: slackObject.toParams(),
        })
          .then(() => {
            this.shareDialogService.close();
            this.toastService.success('Shared to Slack');
          })
          .catch(() => {
            this.toastService.error('Slack message failed');
          });
      },
    });
  }

  handleExportClick() {
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: 'Report View',
      action: 'Export',
    });
  }

  handlePdfExportClick() {
    this.analyticsService.track(AnalyticsEvent.Name.MenuItemSelected, {
      location: 'Report View',
      context: 'report',
      context_id: this.report.token,
      action: 'Export',
      menu_selection: 'PDF',
    });
  }

  isDisabledOption(option) {
    // For Save options, option should be enabled unless there are unsaved changes
    if (option.get('id') === MenuOptionId.Report.Save) {
      return !this.hasUnsavedChanges;
    }

    // For all other options, check isEnabled status
    return !option.isEnabled();
  }

  /**
   *
   * Delegating dropdown click events is a hacky way to prevent us from
   * having to duplicate the inline templates used for the
   * desktop {@link DisplayMode.Menu}.
   * @param $event
   * @param $menuOptionId
   */
  mobileMenuClicked($event, $menuOptionId) {
    const $targetOption = this.$element.find(
      `[data-app-action-id="${$menuOptionId}"] [data-platform-header-menu-action]`
    );
    const $globalTargetOption = this.$window.document.querySelector(
      `.dropdown-menu [data-app-action-id="${$menuOptionId}"] a`
    );

    this.$timeout(() => {
      angular.element($targetOption.length ? $targetOption[0] : $globalTargetOption).trigger('click');
    });
  }

  handleMobileMenuToggle($isOpen) {
    this.onMobileMenuToggle({ $isOpen });
  }

  /**
   * Determine if report was run over 24 hours ago
   * @returns {boolean}
   */
  reportRunIsStale(): boolean {
    return this.reportRunAt && this.reportRunAt.valueOf() < Date.now() - 24 * 60 * 60 * 1000;
  }

  /**
   * @returns {any | OrderedMap<any, any> | OrderedMap<string, any>}
   */
  navSchema(displayMode = DisplayMode.Menu) {
    const isAnalystStudio = this.thoughtspotIntegrated;

    const viewReportOrDetails = isAnalystStudio
      ? [MenuOptionId.Report.ViewDetails, { name: 'View details' }]
      : [MenuOptionId.Report.ViewReport, { name: 'View' }];

    const schemaMap: MenuSchema = {
      [PlatformHeaderNavMenuType.Report]: {
        [DisplayMode.Menu]: OrderedMap([
          [MenuOptionId.Report.ViewDetails],
          [MenuOptionId.Report.ViewReport],
          [MenuOptionId.Report.Edit],
          [MenuOptionId.Report.Share],
          [MenuOptionId.Report.SimpleShare],
          [MenuOptionId.Report.ExportAll],
          [
            MenuOptionId.Report.Export,
            {
              children: [
                [MenuOptionId.Report.ExportCsv],
                [MenuOptionId.Report.ExportPdf],
                [MenuOptionId.Report.ExportPdfDownload],
              ],
            },
          ],
          [MenuOptionId.Report.Subscribe],
          [MenuOptionId.Report.Clone],
          [MenuOptionId.Report.CloneLoggedOut],
          [MenuOptionId.Report.Embed],
          [MenuOptionId.Report.Fullscreen],
        ]) as OrderedMap<MenuOptionId.Report, any>,

        [DisplayMode.Dropdown]: OrderedMap([
          [MenuOptionId.Report.Run],
          [MenuOptionId.Report.ViewDetails],
          [MenuOptionId.Report.ViewReport],
          [MenuOptionId.Report.Edit],
          [MenuOptionId.Report.Share],
          [MenuOptionId.Report.SimpleShare],
          [MenuOptionId.Report.ExportAll],
          [MenuOptionId.Report.ExportCsv, { name: 'Export CSV' }],
          [MenuOptionId.Report.ExportPdf, { name: 'Export PDF' }],
          [MenuOptionId.Report.ExportPdfDownload, { name: 'Export PDF' }],
          [MenuOptionId.Report.Subscribe],
          [MenuOptionId.Report.Clone],
          [MenuOptionId.Report.CloneLoggedOut],
          [MenuOptionId.Report.Embed],
          [MenuOptionId.Report.Fullscreen],
        ]) as OrderedMap<MenuOptionId.Report, any>,
      },

      [PlatformHeaderNavMenuType.Editor]: {
        [DisplayMode.Menu]: OrderedMap([
          [
            MenuOptionId.Report.ManageReport,
            {
              children: [
                [MenuOptionId.Report.Save],
                [MenuOptionId.Report.Sync],
                [MenuOptionId.Report.ViewGithub],
                [MenuOptionId.Report.Move],
                [MenuOptionId.Report.CreateShortcut],
                [MenuOptionId.Report.Clone],
                [MenuOptionId.Report.Rename],
                [MenuOptionId.Report.Settings],
                [MenuOptionId.Report.Delete],
              ],
            },
          ],
          [MenuOptionId.Report.Embed],
          [MenuOptionId.Report.Share],
          [MenuOptionId.Report.Schedule],
          viewReportOrDetails,
        ]) as OrderedMap<MenuOptionId.Report, any>,

        [DisplayMode.Dropdown]: OrderedMap([
          [MenuOptionId.Report.Save],
          [MenuOptionId.Report.Sync],
          [MenuOptionId.Report.ViewGithub],
          [MenuOptionId.Report.Move],
          [MenuOptionId.Report.CreateShortcut],
          [MenuOptionId.Report.Rename],
          [MenuOptionId.Report.Clone],
          [MenuOptionId.Report.Settings],
          [MenuOptionId.Report.Delete],
          [MenuOptionId.Report.Embed],
          [MenuOptionId.Report.Share],
          [MenuOptionId.Report.Schedule],
          viewReportOrDetails,
        ]) as OrderedMap<MenuOptionId.Report, any>,
      },

      [PlatformHeaderNavMenuType.DefinitionEditor]: {
        [DisplayMode.Menu]: OrderedMap([
          [MenuOptionId.Definition.Create],
          [MenuOptionId.Definition.Settings],
          [MenuOptionId.Definition.Sync],
          [MenuOptionId.Definition.ViewGithub],
          [MenuOptionId.Definition.Delete],
        ]) as OrderedMap<MenuOptionId.Definition, any>,

        [DisplayMode.Dropdown]: OrderedMap([
          [MenuOptionId.Definition.Create],
          [MenuOptionId.Definition.Settings],
          [MenuOptionId.Definition.Sync],
          [MenuOptionId.Definition.ViewGithub],
          [MenuOptionId.Definition.Delete],
        ]) as OrderedMap<MenuOptionId.Definition, any>,
      },
    };

    return schemaMap[this.navType][displayMode] || OrderedMap([]);
  }

  // option helpers
  optionIs(option: AnyMenuOptionType, id: MenuOptionId.AllTypes) {
    return option && option.get('id') === id;
  }

  resolveOption(option: List<AnyMenuOptionType>) {
    if (this.optionHasChildren(option)) {
      return this.getIndexOption(option);
    } else {
      return option;
    }
  }

  optionHasChildren(option: any): boolean {
    return List.isList(option);
  }

  getIndexOption(option: List<AnyMenuOptionType>): AnyMenuOptionType {
    return option.get(0);
  }

  getChildrenOptionsAsArray(option: List<AnyMenuOptionType>): Array<AnyMenuOptionType> {
    return option.slice(1).toArray();
  }

  enterFullScreen() {
    const reportViewer = this.$window.document.querySelector('.report-view-container');
    if (reportViewer) this.store.dispatch(new WindowActions.EnterFullscreen(reportViewer));
  }
  // pdfExport(reportId: string, reportRunId: string, reportTitle: string, $target) {
  //   this.pdfExportDialogService.exportPdf(reportRunId, reportTitle, $target);
  // }

  getClonePath() {
    return (
      this.reportRun &&
      this.reportRun._resource &&
      this.reportRun._resource.hasLink('web_clone') &&
      this.reportRun._resource.links('web_clone').href
    );
  }

  isInlineMobileOption(option: AnyMenuOptionType) {
    return [
      MenuOptionId.Report.Embed,
      MenuOptionId.Report.Fullscreen,
      MenuOptionId.Report.Clone,
      MenuOptionId.Report.CloneLoggedOut,
      MenuOptionId.Report.Delete,
      MenuOptionId.Report.Settings,
      MenuOptionId.Report.Rename,
      MenuOptionId.Report.Move,
      MenuOptionId.Report.Save,
      MenuOptionId.Report.CreateShortcut,
      MenuOptionId.Report.ViewReport,
      MenuOptionId.Report.ViewDetails,
      MenuOptionId.Report.Edit,
      MenuOptionId.Report.ExportCsv,
      MenuOptionId.Report.ExportPdf,
      MenuOptionId.Report.ExportPdfDownload,
      MenuOptionId.Report.ViewGithub,
      MenuOptionId.Definition.ViewGithub,
    ].some((id: MenuOptionId.AllTypes) => this.optionIs(option, id));
  }

  runButtonTooltipText(isEnabled: boolean): string {
    if (isEnabled && this.expectedRuntime) {
      return 'Usually finishes in ' + formatDistance(this.expectedRuntime, 0);
    } else {
      return 'Running this report has been disabled. It will \n continue to run on a schedule.';
    }
  }

  refreshButtonTooltipCopy(isEnabled: boolean, datasetCount, queryCount) {
    if (isEnabled) {
      if (datasetCount > 0 && queryCount === 0) {
        return 'Check for any Dataset updates';
      }
      if (queryCount > 0 && datasetCount === 0) {
        return 'Run all queries in the Report';
      }
      if (queryCount > 0 && datasetCount > 0) {
        return 'Check for any Dataset updates and run all queries in the Report';
      }
      if (datasetCount === 0 && queryCount === 0) {
        return 'Refreshing is disabled from this view';
      }
    } else {
      return 'Refreshing is disabled from this view.  The data can continue to refresh on a schedule.';
    }
  }

  private createEmailParams({
    recipients,
    message,
    options,
  }: {
    recipients: string[];
    message: string;
    options: string[];
  }) {
    return {
      'report_run_email.recipient_address': recipients.join(', '),
      'report_run_email.message': message,
      'report_run_email.data_previews_enabled': options.some((option) => option === 'data_previews_enabled'),
      'report_run_email.data_tables_enabled': options.some((option) => option === 'data_tables_enabled'),
      'report_run_email.report_links_enabled': options.some((option) => option === 'report_links_enabled'),
      'report_run_email.csv_attachments_enabled': options.some((option) => option === 'csv_attachments_enabled'),
      'report_run_email.pdf_attachments_enabled': options.some((option) => option === 'pdf_attachments_enabled'),
    };
  }

  handleReportGithubSync(): void {
    this.githubFacade.dispatch(GithubFacade.Actions.reportEditorHeaderPushedChangesToGithub());
  }

  handleDefinitionGithubSync(): void {
    this.githubFacade.dispatch(GithubFacade.Actions.definitionEditorHeaderPushedChangesToGithub());
  }
  $onDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
